import React, { useRef, useId } from 'react';
import { Bar, BarChart as RechartsBarChart, XAxis, YAxis, CartesianGrid, ComposedChart } from 'recharts';
import { chartDefaults, ISimpleBarChart } from '../SimpleChartEngine/types';
import { ChartContainer, ChartLegend, ChartTooltip } from '@/components/ui/chart';
import { AIChartEngineCustomLegendContent, generateChartParams } from '../SimpleChartEngine/SimpleChartEngine';
import { SimpleChartCardWrapper } from './SimpleChartCardWrapper';
import { SimpleChartTooltip } from './SimpleChartTooltip';

export const SimpleBarChart: React.FC<ISimpleBarChart> = (props) => {
  const { data, stacked, horizontal, xAxisKey = chartDefaults.xAxisKey, yAxisKey } = props;
  const chartContainerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const chartId = useId(); // Generate unique ID for this chart instance

  const { chartOptions, chartDataKeys, chartColors, chartConfig } = generateChartParams(props);
  const ChartComponent = stacked ? ComposedChart : RechartsBarChart;

  // Helper function to wrap text at a specified width (in characters)
  const wrapLabelText = (text: string, maxCharsPerLine: number = 20): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if ((currentLine + (currentLine ? ' ' : '') + word).length <= maxCharsPerLine) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        let remaining = word;
        while (remaining.length > maxCharsPerLine) {
          lines.push(remaining.slice(0, maxCharsPerLine));
          remaining = remaining.slice(maxCharsPerLine);
        }
        currentLine = remaining;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  };

  return (
    <SimpleChartCardWrapper {...props}>
      <ChartContainer
        ref={chartContainerRef}
        config={chartConfig}
        style={{ maxHeight: chartOptions.maxHeight, minHeight: chartOptions.minHeight, width: '100%' }}
      >
        <ChartComponent
          data={data}
          layout={!horizontal ? 'horizontal' : 'vertical'}
          margin={chartOptions.wrapXAxisLabels ? { bottom: 40 } : undefined}
        >
          {
            chartOptions?.showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                horizontal={!horizontal}
                vertical={horizontal}
              />
            )}
          <XAxis
            type={horizontal ? 'number' : 'category'}
            dataKey={horizontal ? yAxisKey : xAxisKey}
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tick={chartOptions.wrapXAxisLabels ? ({ x, y, payload }) => {
              const maxLines = chartOptions.maxLines || 3;
              const maxCharsPerLine = chartOptions.maxCharsPerLine || 15;
              const lines = wrapLabelText(payload.value, maxCharsPerLine).slice(0, maxLines);
              return (
                <g transform={`translate(${x},${y})`}>
                  {lines.map((line: string, i: number) => (
                    <text key={i} x={0} y={i * 15} dy={16} textAnchor="middle" fill="currentColor" fontSize={12}>
                      {line}
                    </text>
                  ))}
                  {payload.value.length > maxCharsPerLine * maxLines && (
                    <text x={0} y={maxLines * 15} dy={16} textAnchor="middle" fill="currentColor" fontSize={12}>
                      ...
                    </text>
                  )}
                </g>
              );
            } : undefined}
            interval={chartOptions.wrapXAxisLabels ? 0 : undefined}
          />
          <YAxis
            type={horizontal ? 'category' : 'number'}
            stroke="hsl(var(--muted-foreground))"
            fontSize={12}
            tickFormatter={chartOptions.yAxisTickFormatter}
            width={chartOptions.yAxisWidth}
          />

          {chartOptions.showLegend && (
            <ChartLegend content={<AIChartEngineCustomLegendContent config={chartConfig} />} />
          )}

          <ChartTooltip
            content={(props) => (
              <SimpleChartTooltip
                {...props}
                options={chartOptions}
                config={chartConfig}
                chartContainerRef={chartContainerRef}
              />
            )}
            // content={
            //   <AIChartEngineCustomTooltipContent
            //     options={chartOptions}
            //     config={chartConfig} />
            // }
            cursor={false} />

          {chartOptions.areaGradient && (
            <defs>
              {chartDataKeys.map((key, index) => (
                <linearGradient
                  id={`barGradient-${chartId}-${index}`}
                  key={key}
                  x1={horizontal ? "0" : "0"}
                  y1={horizontal ? "0" : "0"}
                  x2={horizontal ? "1" : "0"}
                  y2={horizontal ? "0" : "1"}
                >
                  <stop offset="0%" stopColor={chartColors[index]} stopOpacity={chartOptions.areaOpacityFrom ?? 0.6} />
                  <stop offset="100%" stopColor={chartColors[index]} stopOpacity={chartOptions.areaOpacityTo ?? 0.2} />
                </linearGradient>
              ))}
            </defs>
          )}

          {chartDataKeys.map((key, index) => {
            // Calculate radius based on stacking and position
            // For stacked: bottom bar (first) gets bottom corners, top bar (last) gets top corners
            // For non-stacked: all bars get top corners rounded
            // Radius format: [top-right, bottom-right, bottom-left, top-left]
            let radius: [number, number, number, number] = [4, 4, 0, 0]; // Default: top corners rounded

            if (stacked) {
              const isFirst = index === 0;
              const isLast = index === chartDataKeys.length - 1;

              if (isFirst && isLast) {
                // Only one bar - round all corners
                radius = [4, 4, 4, 4];
              } else if (isFirst) {
                // Bottom bar - round bottom corners
                radius = [0, 0, 4, 4];
              } else if (isLast) {
                // Top bar - round top corners
                radius = [4, 4, 0, 0];
              } else {
                // Middle bars - no rounding
                radius = [0, 0, 0, 0];
              }
            }

            return (
              <Bar
                key={key}
                dataKey={key}
                fill={chartOptions.areaGradient ? `url(#barGradient-${chartId}-${index})` : chartColors[index]}
                stroke={chartColors[index]}
                strokeWidth={chartOptions.strokeWidth ?? 1}
                radius={radius}
                stackId={stacked ? 'stack' : undefined}
                barSize={chartOptions.barSize}
              />
            );
          })}
        </ChartComponent>
      </ChartContainer>
    </SimpleChartCardWrapper>
  );
};
